/**
 * Grandfather (Regra N1) das roles do wazir-web que deixaram de ser compartilhadas.
 *
 * Contexto (specs/wazir/auditoria-roles-wazir-web.md): varios botoes/telas usavam a
 * MESMA role (ex.: `MENU_SETTINGS` em 8 nos, `MENU_DOWNLOADS` em 2, `SYSTEM_EDIT_BUTTON`
 * em Solucoes + Grupo de Permissoes). Cada tela/botao agora tem role PROPRIA. Quem tinha a
 * role antiga NAO pode perder acesso: enquanto o usuario ainda nao recebeu nenhuma das roles
 * novas da "familia" (a migration aditiva do nexus-api ainda nao rodou / o tenant ainda nao
 * foi propagado), a role antiga continua valendo para as telas/botoes que dela se
 * desmembraram. Assim que ele tiver QUALQUER role nova da familia (migration ou admin),
 * o gate passa a exigir a role propria da tela (revogar passa a funcionar).
 *
 * Remover uma familia daqui so na fase de limpeza, depois de o nexus-api ter concedido as
 * roles novas em todos os tenants Wazir (com prova N1) e com OK do P.O.
 */
export interface LegacyRoleFamily {
  id: string;
  description: string;
  /**
   * true  -> o fallback so vale enquanto o usuario nao tem NENHUMA role sucessora da familia
   *          (roles novas desmembradas; permite revogar depois da migracao).
   * false -> alias permanente OU-logico (sem trava): so a heranca OS/Inicio das telas futuras.
   */
  lockWhenMigrated: boolean;
  /**
   * GRANDFATHER (fase 1, P.O. 08/10): a familia so vale quando o login devolveu
   * `legacyScreenAlias !== false` (usuario existente ou em grupo existente, chave
   * WAZIR_PERMISSIONS_V2 OFF). Perfil novo ou chave ON = so a role propria. Campo ausente
   * (wazir-api antigo) = vale, como hoje.
   */
  requiresLegacyScreenAlias?: boolean;
  /**
   * Escopo da trava (so vale com lockWhenMigrated=true). Padrao 'family': qualquer role nova
   * da familia desliga o legado da familia inteira. 'pair': o legado so deixa de valer para uma
   * role nova especifica quando ELA propria esta presente (ou seja, nunca por presenca de outra
   * da familia) — obrigatorio quando as sucessoras JA existiam no catalogo (orfas) e podem estar
   * parcialmente concedidas sem que isso signifique migracao.
   */
  lockScope?: 'family' | 'pair';
  /** role sucessora (completa, com sufixo) -> roles legadas (completas) que a concediam. */
  successors: Record<string, string[]>;
}

const menuSuccessors = (
  base: string,
  legacyBases: string[]
): Record<string, string[]> => ({
  [`${base}_SHOW`]: legacyBases.map((l) => `${l}_SHOW`),
  [`${base}_ENABLE`]: legacyBases.map((l) => `${l}_ENABLE`),
});

const merge = (...maps: Record<string, string[]>[]): Record<string, string[]> =>
  Object.assign({}, ...maps);

export const LEGACY_ROLE_FAMILIES: readonly LegacyRoleFamily[] = [
  {
    id: 'menu-settings-split',
    description:
      'MENU_SETTINGS (8 nos) -> Configuracoes mantem MENU_SETTINGS; Cadastro Base e seus 6 filhos ganham role propria por caminho.',
    lockWhenMigrated: true,
    successors: merge(
      menuSuccessors('MENU_BASE_REGISTRATION', ['MENU_SETTINGS']),
      menuSuccessors('MENU_BASE_REGISTRATION_SUBMENU_ACCOUNT_TYPES', ['MENU_SETTINGS']),
      menuSuccessors('MENU_BASE_REGISTRATION_SUBMENU_PAYMENT_METHODS', ['MENU_SETTINGS']),
      menuSuccessors('MENU_BASE_REGISTRATION_SUBMENU_VALUE_REFERENCES', ['MENU_SETTINGS']),
      menuSuccessors('MENU_BASE_REGISTRATION_SUBMENU_ACCOUNT_MOVEMENTATIONS', ['MENU_SETTINGS']),
      menuSuccessors('MENU_BASE_REGISTRATION_SUBMENU_CUSTOMER_ACCESS', ['MENU_SETTINGS']),
      menuSuccessors('MENU_BASE_REGISTRATION_SUBMENU_GLOBAL_CONFIG', ['MENU_SETTINGS'])
    ),
  },
  {
    id: 'menu-downloads-split',
    description:
      'MENU_DOWNLOADS (grupo + folha Solucoes) -> a folha Solucoes passa a MENU_DOWNLOADS_SUBMENU_SOLUTIONS.',
    lockWhenMigrated: true,
    lockScope: 'pair', // sucessoras ja existiam como orfas no catalogo (N1)
    successors: menuSuccessors('MENU_DOWNLOADS_SUBMENU_SOLUTIONS', ['MENU_DOWNLOADS']),
  },
  {
    id: 'menu-dre-rename',
    description: 'MENU_DRE_REPORTS renomeada para o caminho MENU_FINANCIAL_SUBMENU_DRE_REPORTS.',
    lockWhenMigrated: true,
    successors: menuSuccessors('MENU_FINANCIAL_SUBMENU_DRE_REPORTS', ['MENU_DRE_REPORTS']),
  },
  {
    id: 'menu-os-list-kanban-split',
    description:
      'MENU_OS cobria o grupo e as folhas Lista/Kanban. As folhas passam a MENU_OS_SUBMENU_LIST e MENU_OS_SUBMENU_KANBAN.',
    lockWhenMigrated: true,
    lockScope: 'pair',
    successors: merge(
      menuSuccessors('MENU_OS_SUBMENU_LIST', ['MENU_OS']),
      menuSuccessors('MENU_OS_SUBMENU_KANBAN', ['MENU_OS'])
    ),
  },
  {
    id: 'button-customer-acess-vs-solution',
    description:
      'Acessos de Clientes usava SOLUTION_*_BUTTON (de Downloads > Solucoes); agora CUSTOMER_ACESS_*_BUTTON.',
    lockWhenMigrated: true,
    lockScope: 'pair', // sucessoras ja existiam como orfas no catalogo (N1)
    successors: {
      CUSTOMER_ACESS_EDIT_BUTTON_SHOW: ['SOLUTION_EDIT_BUTTON_SHOW'],
      CUSTOMER_ACESS_EDIT_BUTTON_ENABLE: ['SOLUTION_EDIT_BUTTON_ENABLED'],
      CUSTOMER_ACESS_DELETE_BUTTON_SHOW: ['SOLUTION_DELETE_BUTTON_SHOW'],
      CUSTOMER_ACESS_DELETE_BUTTON_ENABLED: ['SOLUTION_DELETE_BUTTON_ENABLED'],
    },
  },
  {
    id: 'button-permission-group-vs-system',
    description:
      'Grupo de Permissoes usava SYSTEM_*_BUTTON (de Licencas > Solucoes); agora PERMISSION_GROUP_*_BUTTON.',
    lockWhenMigrated: true,
    successors: {
      PERMISSION_GROUP_EDIT_BUTTON_SHOW: ['SYSTEM_EDIT_BUTTON_SHOW'],
      PERMISSION_GROUP_EDIT_BUTTON_ENABLED: ['SYSTEM_EDIT_BUTTON_ENABLED'],
      PERMISSION_GROUP_DELETE_BUTTON_SHOW: ['SYSTEM_DELETE_BUTTON_SHOW'],
      PERMISSION_GROUP_DELETE_BUTTON_ENABLED: ['SYSTEM_DELETE_BUTTON_ENABLED'],
    },
  },
  {
    // Industria/Faturamento/Caixa/Fiscal (telas futuras) herdam a visibilidade de OS ou Inicio.
    // P.O. 30/09: telas futuras valem so pela role propria — MAS, pelo GRANDFATHER da fase 1
    // (P.O. 08/10), quem ja existe mantem exatamente o acesso de hoje: a heranca continua para
    // usuario existente (ou em grupo existente) e sai so para perfil novo ou com a chave
    // WAZIR_PERMISSIONS_V2 ligada (`legacyScreenAlias=false` no login).
    id: 'menu-placeholders-inherit-os-home',
    description:
      'Industria/Faturamento/Caixa/Fiscal herdam a visibilidade de OS ou Inicio so para quem ja existia (grandfather).',
    lockWhenMigrated: false,
    requiresLegacyScreenAlias: true,
    successors: merge(
      ...[
        'MENU_INDUSTRY',
        'MENU_BILLING',
        'MENU_BILLING_SUBMENU_SALES',
        'MENU_BILLING_SUBMENU_BUDGETS',
        'MENU_BILLING_SUBMENU_ORDERS',
        'MENU_CASHIER',
        'MENU_FISCAL',
      ].map((b) => menuSuccessors(b, ['MENU_OS', 'MENU_HOME']))
    ),
  },
  {
    // Configuracoes > Integracoes > Barid nasceu sem role propria (valia por MENU_SETTINGS).
    // Ganhou role de tela + uma por botao; enquanto o usuario nao recebeu nenhuma delas
    // (catalogo ainda nao propagado ao tenant), MENU_SETTINGS continua liberando a tela.
    id: 'barid-settings-split',
    description:
      'Barid (Configuracoes > Integracoes) valia por MENU_SETTINGS; agora MENU_SETTINGS_SUBMENU_BARID e BARID_*_BUTTON.',
    lockWhenMigrated: true,
    successors: merge(
      menuSuccessors('MENU_SETTINGS_SUBMENU_BARID', ['MENU_SETTINGS']),
      ...[
        'BARID_INSTALLER_DOWNLOAD_BUTTON',
        'BARID_DEVICE_PAIR_BUTTON',
        'BARID_DEVICE_NEW_CODE_BUTTON',
        'BARID_DEVICE_RENAME_BUTTON',
        'BARID_DEVICE_DELETE_BUTTON',
        'BARID_TEF_SAVE_BUTTON',
        'BARID_PRINTER_SYNC_BUTTON',
        'BARID_PRINTER_TEST_BUTTON',
        'BARID_PRINTER_SHARE_BUTTON',
        'BARID_JOB_RESEND_BUTTON',
      ].map((button) => ({
        [`${button}_SHOW`]: ['MENU_SETTINGS_SHOW'],
        [`${button}_ENABLED`]: ['MENU_SETTINGS_ENABLE'],
      }))
    ),
  },
  {
    // F3.7 fatia 2: fila de impressao e redirecionar. Familia PROPRIA (nao a barid-settings-split):
    // quem ja migrou para as roles da fatia 1 (ou recebeu o grandfather do Nexus) nao perde estes
    // botoes. A familia so trava quando o usuario recebe uma role DELA.
    id: 'barid-queue-split',
    description:
      'Fila de impressao e redirecionar do Barid valiam por MENU_SETTINGS; agora BARID_DEVICE_PRINT_QUEUE_BUTTON e BARID_JOB_REDIRECT_BUTTON.',
    lockWhenMigrated: true,
    successors: merge(
      ...['BARID_DEVICE_PRINT_QUEUE_BUTTON', 'BARID_JOB_REDIRECT_BUTTON'].map((button) => ({
        [`${button}_SHOW`]: ['MENU_SETTINGS_SHOW'],
        [`${button}_ENABLED`]: ['MENU_SETTINGS_ENABLE'],
      }))
    ),
  },
  {
    // F3.7 fatia 3: "Abrir painel do Barid neste computador". Familia PROPRIA pelo mesmo motivo da
    // barid-queue-split: quem ja migrou para as roles das fatias 1/2 (ou recebeu o grandfather do
    // Nexus) nao perde o botao novo; a familia so trava quando o usuario recebe uma role DELA.
    id: 'barid-local-panel-split',
    description:
      'Abrir o painel local do Barid valia por MENU_SETTINGS; agora BARID_LOCAL_PANEL_BUTTON.',
    lockWhenMigrated: true,
    successors: {
      BARID_LOCAL_PANEL_BUTTON_SHOW: ['MENU_SETTINGS_SHOW'],
      BARID_LOCAL_PANEL_BUTTON_ENABLED: ['MENU_SETTINGS_ENABLE'],
    },
  },
  {
    // F3.7 fatia 4: Central de Impressoes (folha de topo do menu, logo apos Configuracoes; rota print-center).
    // Familia PROPRIA pelo mesmo motivo da barid-queue-split: quem ja migrou para as roles das
    // fatias 1-3 (ou recebeu o grandfather do Nexus) nao perde a tela nova; a familia so trava
    // quando o usuario recebe uma role DELA.
    id: 'print-center-split',
    description:
      'Central de Impressoes valia por MENU_SETTINGS; agora MENU_PRINT_CENTER e PRINT_CENTER_JOB_*_BUTTON.',
    lockWhenMigrated: true,
    successors: merge(
      menuSuccessors('MENU_PRINT_CENTER', ['MENU_SETTINGS']),
      ...['PRINT_CENTER_JOB_RESEND_BUTTON', 'PRINT_CENTER_JOB_REDIRECT_BUTTON'].map((button) => ({
        [`${button}_SHOW`]: ['MENU_SETTINGS_SHOW'],
        [`${button}_ENABLED`]: ['MENU_SETTINGS_ENABLE'],
      }))
    ),
  },
  {
    // F3.7 fatia 5: "Trocar o computador" do terminal (assistente) e "Terminais sem computador ha N
    // dias" (Configuracoes > Integracoes > Barid). Familia PROPRIA pelo mesmo motivo da
    // barid-queue-split: quem ja migrou para as roles das fatias 1-4 (ou recebeu o grandfather do
    // Nexus) nao perde os botoes novos; a familia so trava quando o usuario recebe uma role DELA.
    id: 'barid-computer-swap-split',
    description:
      'Trocar o computador do terminal do Barid valia por MENU_SETTINGS; agora BARID_TERMINAL_REPLACE_COMPUTER_BUTTON e BARID_REPLACE_COMPUTER_CANDIDATES_BUTTON.',
    lockWhenMigrated: true,
    successors: merge(
      ...['BARID_TERMINAL_REPLACE_COMPUTER_BUTTON', 'BARID_REPLACE_COMPUTER_CANDIDATES_BUTTON'].map((button) => ({
        [`${button}_SHOW`]: ['MENU_SETTINGS_SHOW'],
        [`${button}_ENABLED`]: ['MENU_SETTINGS_ENABLE'],
      }))
    ),
  },
  {
    // Padrao ABCX na tela do Barid: "Testar conexao do agente" e "Backup/Restaurar" da configuracao do terminal
    // (Configuracoes > Integracoes > Barid). Familia PROPRIA pelo mesmo motivo da barid-queue-split: quem ja migrou
    // para as roles das fatias anteriores nao perde os botoes novos; a familia so trava quando o usuario recebe uma
    // role DELA. So adicao: nenhuma role existente muda.
    id: 'barid-config-backup-split',
    description:
      'Testar a conexao do agente e backup/restauracao da configuracao do terminal do Barid valiam por MENU_SETTINGS; agora BARID_AGENT_TEST_BUTTON, BARID_CONFIG_BACKUP_BUTTON e BARID_CONFIG_RESTORE_BUTTON.',
    lockWhenMigrated: true,
    successors: merge(
      ...['BARID_AGENT_TEST_BUTTON', 'BARID_CONFIG_BACKUP_BUTTON', 'BARID_CONFIG_RESTORE_BUTTON'].map((button) => ({
        [`${button}_SHOW`]: ['MENU_SETTINGS_SHOW'],
        [`${button}_ENABLED`]: ['MENU_SETTINGS_ENABLE'],
      }))
    ),
  },
  {
    // Configuracoes > Integracoes > Impressoras e Configuracoes > Dispositivos (telas separadas do Barid) +
    // areas de impressao e apelido da impressora. Familia PROPRIA: quem ja migrou outras familias nao perde
    // as telas novas; so trava quando o usuario recebe uma role DELA. So adicao.
    id: 'barid-printers-devices-split',
    description:
      'Impressoras e Dispositivos do Barid valiam por MENU_SETTINGS; agora MENU_SETTINGS_SUBMENU_PRINTERS, MENU_SETTINGS_SUBMENU_DEVICES, BARID_PRINT_AREAS_SAVE_BUTTON e BARID_PRINTER_RENAME_BUTTON.',
    lockWhenMigrated: true,
    successors: merge(
      menuSuccessors('MENU_SETTINGS_SUBMENU_PRINTERS', ['MENU_SETTINGS']),
      menuSuccessors('MENU_SETTINGS_SUBMENU_DEVICES', ['MENU_SETTINGS']),
      ...['BARID_PRINT_AREAS_SAVE_BUTTON', 'BARID_PRINTER_RENAME_BUTTON', 'BARID_CONNECTIVITY_INDICATOR'].map((button) => ({
        [`${button}_SHOW`]: ['MENU_SETTINGS_SHOW'],
        [`${button}_ENABLED`]: ['MENU_SETTINGS_ENABLE'],
      }))
    ),
  },
  {
    id: 'menu-products-split',
    description:
      'Cadastro de produtos nasce em Cadastro Base e vale por MENU_SETTINGS ate a role propria ser concedida. Familia separada para quem ja migrou as outras telas do cadastro continuar vendo Produtos.',
    lockWhenMigrated: true,
    successors: menuSuccessors('MENU_BASE_REGISTRATION_SUBMENU_PRODUCTS', ['MENU_SETTINGS']),
  },
];

/** `legacyScreenAlias` do usuario logado (`@gmt:user`); ausente/ilegivel = true (acesso de hoje). */
export function storedLegacyScreenAlias(): boolean {
  try {
    const raw = window.localStorage.getItem('@gmt:user');
    return raw ? JSON.parse(raw)?.legacyScreenAlias !== false : true;
  } catch {
    return true;
  }
}

const FAMILY_BY_ROLE: ReadonlyMap<string, LegacyRoleFamily> = new Map(
  LEGACY_ROLE_FAMILIES.flatMap((family) =>
    Object.keys(family.successors).map((role) => [role, family] as [string, LegacyRoleFamily])
  )
);

/** Roles legadas que ainda concedem `role` (lista vazia se a role nao tem legado). */
export function legacyRolesOf(role: string): string[] {
  const family = FAMILY_BY_ROLE.get(role);
  return family ? family.successors[role] : [];
}

/**
 * `true` quando a role legada correspondente concede `role` na transicao (Regra N1).
 * Nao considera a role propria: o chamador ja testou `permissions.includes(role)`.
 */
export function hasLegacyRoleFallback(
  permissions: readonly string[] | null | undefined,
  role: string,
  options: { legacyScreenAlias?: boolean } = {}
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }
  const family = FAMILY_BY_ROLE.get(role);
  if (!family) {
    return false;
  }
  if (family.requiresLegacyScreenAlias && (options.legacyScreenAlias ?? storedLegacyScreenAlias()) === false) {
    return false;
  }
  if (family.lockWhenMigrated && family.lockScope !== 'pair') {
    const alreadyMigrated = Object.keys(family.successors).some((successor) =>
      permissions.includes(successor)
    );
    if (alreadyMigrated) {
      return false;
    }
  }
  return family.successors[role].some((legacy) => permissions.includes(legacy));
}
